import React, { useEffect, useState } from "react";
import { ChevronDown, ChevronRight, Newspaper, Sparkles, X } from "lucide-react";
import { toast } from "sonner";
import { NEWSROOM_PRESETS } from "./AdvancedDirectorPanel";

/**
 * NewsroomControls — collapsible set of Lower-Third / Breaking / Ticker
 * controls + one-tap presets, designed to live in the right Monitors column
 * of DirectorConsole.
 *
 * Reads/writes overlay state via the broadcaster proxy exposed by
 * AdvancedDirectorPanel (`getOverlays`, `setOverlayField`, `applyNewsroomPreset`,
 * `clearOverlays`). Polls `getOverlays()` at 400ms so the UI stays in sync
 * across scene changes / preset applies.
 *
 * Props:
 *   broadcasterProxy: ref value from AdvancedDirectorPanel.onBroadcasterReady
 *   defaultOpen?: boolean
 */
export default function NewsroomControls({ broadcasterProxy, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  const [overlays, setOverlays] = useState({
    lower_third: { visible: false, name: "", title: "" },
    breaking: { visible: false, text: "" },
    ticker: { visible: false, text: "", speed: 30 },
  });

  // Pull overlay state from the director panel so this component reflects the
  // actual overlays rendered on the stream (including the pip/weather/brb
  // state owned by the panel).
  useEffect(() => {
    if (!broadcasterProxy?.getOverlays) return;
    const tick = () => {
      const o = broadcasterProxy.getOverlays?.();
      if (o) setOverlays((prev) => ({ ...prev, ...o }));
    };
    tick();
    const h = setInterval(tick, 400);
    return () => clearInterval(h);
  }, [broadcasterProxy]);

  const set = (path, val) => {
    broadcasterProxy?.setOverlayField?.(path, val);
    // Optimistic local update so sliders / inputs don't lag behind the 400ms poll
    const [a, b] = path.split(".");
    setOverlays((o) => ({ ...o, [a]: { ...o[a], [b]: val } }));
  };

  const applyPreset = (key) => {
    broadcasterProxy?.applyNewsroomPreset?.(key);
    toast.success(`Preset: ${NEWSROOM_PRESETS[key]?.label || key}`, { id: "news-preset" });
  };

  const clearAll = () => {
    broadcasterProxy?.clearOverlays?.();
    toast.info("Newsroom overlays cleared", { id: "news-clear" });
  };

  return (
    <section
      data-testid="monitors-newsroom"
      className="bg-black/40 border border-white/10 rounded-lg p-2"
    >
      <button
        data-testid="newsroom-toggle"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-black text-amber-300 hover:text-amber-200"
      >
        {open ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
        <Newspaper className="w-3 h-3" /> Newsroom
        {(overlays.lower_third.visible || overlays.breaking.visible || overlays.ticker.visible) && (
          <span
            data-testid="newsroom-active-dot"
            className="ml-auto w-1.5 h-1.5 rounded-full bg-tv-live tv-pulse"
            title="An overlay is live on the stream"
          />
        )}
      </button>

      {open && (
        <div className="mt-2 space-y-2.5">
          {/* One-tap presets */}
          <div>
            <div className="text-[9px] uppercase tracking-wider text-white/50 font-bold mb-1 flex items-center gap-1">
              <Sparkles className="w-2.5 h-2.5 text-tv-accent" /> Presets
            </div>
            <div className="grid grid-cols-2 gap-1">
              {Object.entries(NEWSROOM_PRESETS).map(([key, p]) => (
                <button
                  key={key}
                  type="button"
                  data-testid={`monitors-newsroom-preset-${key}`}
                  onClick={() => applyPreset(key)}
                  className="bg-white/5 hover:bg-tv-accent/25 hover:text-tv-accent border border-white/10 text-white/90 text-[10px] font-bold py-1 rounded transition truncate"
                  title={p.label}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          {/* Lower-Third */}
          <div className="border border-white/10 rounded-md p-1.5 space-y-1">
            <label className="flex items-center gap-1.5 text-[10px] text-white">
              <input
                type="checkbox"
                data-testid="monitors-lower-third-toggle"
                checked={!!overlays.lower_third.visible}
                onChange={(e) => set("lower_third.visible", e.target.checked)}
                className="accent-tv-accent"
              />
              Lower-third
            </label>
            <input
              type="text"
              data-testid="monitors-lower-third-name"
              value={overlays.lower_third.name || ""}
              onChange={(e) => set("lower_third.name", e.target.value)}
              placeholder="Name"
              className="w-full bg-white/5 border border-white/10 rounded px-1.5 py-1 text-white text-[10px] outline-none focus:border-tv-accent"
            />
            <input
              type="text"
              data-testid="monitors-lower-third-title"
              value={overlays.lower_third.title || ""}
              onChange={(e) => set("lower_third.title", e.target.value)}
              placeholder="Title / role"
              className="w-full bg-white/5 border border-white/10 rounded px-1.5 py-1 text-white text-[10px] outline-none focus:border-tv-accent"
            />
          </div>

          {/* Breaking */}
          <div className="border border-white/10 rounded-md p-1.5 space-y-1">
            <label className="flex items-center gap-1.5 text-[10px] text-white">
              <input
                type="checkbox"
                data-testid="monitors-breaking-toggle"
                checked={!!overlays.breaking.visible}
                onChange={(e) => set("breaking.visible", e.target.checked)}
                className="accent-tv-accent"
              />
              Breaking banner
            </label>
            <input
              type="text"
              data-testid="monitors-breaking-text"
              value={overlays.breaking.text || ""}
              onChange={(e) => set("breaking.text", e.target.value)}
              placeholder="Breaking headline…"
              className="w-full bg-white/5 border border-white/10 rounded px-1.5 py-1 text-white text-[10px] outline-none focus:border-tv-accent"
            />
          </div>

          {/* Ticker */}
          <div className="border border-white/10 rounded-md p-1.5 space-y-1">
            <label className="flex items-center gap-1.5 text-[10px] text-white">
              <input
                type="checkbox"
                data-testid="monitors-ticker-toggle"
                checked={!!overlays.ticker.visible}
                onChange={(e) => set("ticker.visible", e.target.checked)}
                className="accent-tv-accent"
              />
              Ticker
            </label>
            <input
              type="text"
              data-testid="monitors-ticker-text"
              value={overlays.ticker.text || ""}
              onChange={(e) => set("ticker.text", e.target.value)}
              placeholder="Scrolling headlines…"
              className="w-full bg-white/5 border border-white/10 rounded px-1.5 py-1 text-white text-[10px] outline-none focus:border-tv-accent"
            />
            <div className="flex items-center gap-1.5 text-[9px] text-white/60">
              <span>Speed</span>
              <input
                type="range"
                data-testid="monitors-ticker-speed"
                min="0"
                max="40"
                value={overlays.ticker.speed ?? 30}
                onChange={(e) => set("ticker.speed", parseInt(e.target.value, 10))}
                className="flex-1 accent-tv-accent"
              />
              <span className="tabular-nums w-4 text-right">{overlays.ticker.speed ?? 30}</span>
            </div>
          </div>

          <button
            data-testid="newsroom-clear"
            onClick={clearAll}
            className="w-full flex items-center justify-center gap-1 bg-red-500/15 hover:bg-red-500/25 text-red-200 border border-red-500/30 text-[10px] font-bold py-1.5 rounded"
          >
            <X className="w-3 h-3" /> Clear all overlays
          </button>
        </div>
      )}
    </section>
  );
}
